import React from "react";
import { Table } from "antd";
import styles from "./KetQuaKsk.module.css";

/**
 * Hai khối "Kết luận" và "Khám sức khỏe" của phiếu khám sức khoẻ định kỳ,
 * dựng theo đúng bố cục màn CateMedicalExaminationDetail bên mobile.
 *
 * Dữ liệu đã có sẵn trong response của get-ls-kcb (khamSucKhoeDinhKy,
 * ketQuaKhamSucKhoe) — trước đây trang web không dùng tới.
 */

const hasText = (v) => !!(v && String(v).trim());

/**
 * Chuẩn hoá khamSucKhoeDinhKy về tên trường dễ đọc.
 *
 * Lớp KhamSucKhoeDinhKy bên backend gắn [JsonPropertyName] viết thường liền
 * nhau ("tenloaisuckhoe", "tuvandieutribv198_noidung"…) nên JSON KHÔNG theo
 * camelCase như các DTO khác. Đọc thẳng ksk.tenLoaiSucKhoe sẽ luôn undefined
 * và cả khối kết luận biến mất — đây từng là nguyên nhân mục này không hiện.
 */
const normalizeKsk = (ksk) => {
  if (!ksk) return null;
  return {
    tenLoaiSucKhoe: ksk.tenloaisuckhoe,
    ketLuanBenhTat: ksk.ketluanbenhtatneuco,
    khuyenCao: ksk.khuyencaophanloaisuckhoe || [],
    advices: [
      {
        title: ksk.tuvandieutribv198_tieude,
        content: ksk.tuvandieutribv198_noidung,
        note: ksk.tuvandieutribv198_ghichu,
      },
      {
        title: ksk.tuvandieutribvcat_tieude,
        content: ksk.tuvandieutribvcat_noidung,
        note: ksk.tuvandieutribvcat_ghichu,
      },
      {
        title: ksk.khamlaibvcat_tieude,
        content: ksk.khamlaibvcat_noidung,
        note: ksk.khamlaibvcat_ghichu,
      },
      { title: ksk.luuy_tieude, content: ksk.luuy_noidung },
    ],
  };
};

/** Một khối tư vấn: tiêu đề + nội dung + ghi chú, bỏ qua nếu rỗng hết. */
const AdviceBlock = ({ title, content, note }) => {
  if (!hasText(content) && !hasText(note)) return null;
  return (
    <div className={styles.adviceBlock}>
      {hasText(title) && <div className={styles.adviceTitle}>{title}</div>}
      {hasText(content) && <div className={styles.adviceText}>{content}</div>}
      {hasText(note) && <div className={styles.adviceNote}>{note}</div>}
    </div>
  );
};

/**
 * Khối kết luận có gì để hiển thị hay không. Export để trang ngoài dùng chung
 * điều kiện khi quyết định hiện tab, tránh tab trỏ tới mục đã tự ẩn.
 */
export const hasConclusionContent = (ksk, ketLuanChung) => {
  if (hasText(ketLuanChung)) return true;
  const n = normalizeKsk(ksk);
  if (!n) return false;
  return !!(
    hasText(n.tenLoaiSucKhoe) ||
    hasText(n.ketLuanBenhTat) ||
    n.khuyenCao.length > 0 ||
    n.advices.some((a) => hasText(a.content) || hasText(a.note))
  );
};

export const HealthConclusion = ({ ksk, ketLuanChung, phanLoaiCuoi }) => {
  if (!hasConclusionContent(ksk, ketLuanChung)) return null;

  const n = normalizeKsk(ksk) || { khuyenCao: [], advices: [] };
  const recs = n.khuyenCao;
  // phanLoaiCuoi từ DTO mới đã là chuỗi "Loại I…"; khamSucKhoeDinhKy là nguồn cũ.
  const classification = phanLoaiCuoi || n.tenLoaiSucKhoe;

  return (
    <div className={styles.card}>
      {hasText(classification) && (
        <div className={styles.classificationRow}>
          <span className={styles.classificationLabel}>Phân loại sức khỏe</span>
          <span className={styles.classificationValue}>{classification}</span>
        </div>
      )}

      {hasText(ketLuanChung) && (
        <div className={styles.block}>
          <div className={styles.blockTitle}>Kết luận</div>
          <div className={styles.blockText}>{ketLuanChung}</div>
        </div>
      )}

      {hasText(n.ketLuanBenhTat) && (
        <div className={styles.block}>
          <div className={styles.blockTitle}>Bệnh tật (nếu có)</div>
          <div className={styles.blockText}>{n.ketLuanBenhTat}</div>
        </div>
      )}

      {recs.length > 0 && (
        <div className={styles.block}>
          <div className={styles.blockTitle}>Khuyến cáo phân loại sức khỏe</div>
          <ul className={styles.recList}>
            {recs.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>
      )}

      {n.advices.map((a, i) => (
        <AdviceBlock
          key={i}
          title={a.title}
          content={a.content}
          note={a.note}
        />
      ))}
    </div>
  );
};

// Backend chỉ trả maLoai dạng số; nhãn nằm trong enum EPhanLoaiKSKCategory
// (Enums.cs) và được mobile dịch sẵn ở tầng API. Web nhận số thô nên phải tự
// dịch, nếu không cột Phân loại sẽ hiện "1", "2" thay vì "Loại I", "Loại II".
const PHAN_LOAI_KSK = {
  1: "Loại I",
  2: "Loại II",
  3: "Loại III",
  4: "Loại IV",
  5: "Loại V",
};

const columns = [
  {
    title: "STT",
    key: "stt",
    width: 56,
    align: "center",
    render: (_v, _r, index) => index + 1,
  },
  {
    title: "Chuyên khoa",
    dataIndex: "chuyenKhoa",
    key: "chuyenKhoa",
  },
  { title: "Kết luận", dataIndex: "ketLuan", key: "ketLuan", width: 400 },
  {
    title: "Phân loại",
    key: "phanLoai",
    align: "center",
    // Hai nguồn dữ liệu đặt tên khác nhau: WareHouseDetailDto trả sẵn phanLoai
    // dạng chuỗi ("Loại I"), còn get-ls-kcb chỉ có maLoai dạng số. Đọc mỗi
    // maLoai thì cột trống khi dùng dữ liệu từ DTO mới.
    render: (_v, r) => r.phanLoai || PHAN_LOAI_KSK[r.maLoai] || "",
  },
];

export const SpecialtyExams = ({ rows = [] }) => {
  if (rows.length === 0) return null;
  return (
    <Table
      className={styles.table}
      columns={columns}
      dataSource={rows}
      rowKey={(r) => r.id}
      pagination={false}
      size="small"
      scroll={{ x: "max-content" }}
    />
  );
};
